import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import api from '../api/axios';
import {
  Loader2, RefreshCw, Search, MapPin, Store, Building2, Users, Globe,
  AlertTriangle, ExternalLink, Link2, Phone, Star, X, SlidersHorizontal, Check,
  ChevronRight, ArrowUpDown, ImageOff, Image as ImageIcon, BarChart3, Download,
} from 'lucide-react';
import GbpLocationModal from '../components/gbp/GbpLocationModal';
import GbpReportTab from '../components/gbp/GbpReportTab';
import { DAY_ORDER, DAY_TR, MEDIA_CATEGORY_ORDER, catTR, statusTR } from '../components/gbp/gbpLabels';

/**
 * GbpLocations — Google Business Profile lokasyonları (marka bazlı)
 * Route: /gbp/locations
 *
 * Seçili markanın GBP bağlantısıyla erişilebilen lokasyonlar listelenir.
 * Kapsam backend'de belirlenir: her istek X-Brand-ID ile gider, backend
 * kullanıcının o markaya erişimini DB'den doğrular ve yalnızca o markanın
 * ApiConnection(platform:'GBP') kaydını kullanır.
 *
 * Bir Google hesabı birkaç müşterinin lokasyon grubuna birden davetli olabilir;
 * "Hesap kapsamı" ile markaya hangi GBP hesaplarının ait olduğu kalıcı olarak
 * seçilir (PUT /gbp-brand/scope). Seçim yoksa erişilen her hesap listelenir.
 *
 * Puanlar ve fotoğraflar lokasyon başına birer v4 çağrısı gerektirdiği için
 * ayrı uçlardan, liste geldikten SONRA ve birbiri ardına yüklenir (aynı anda
 * koşarlarsa v4'ün dakikalık kotası zorlanır).
 *
 * Veri CANLI API'den gelir (DB'ye yazılmaz). accounts.list bir kişisel hesabın
 * yanı sıra location group / user group / organization da döndürebilir; bunların
 * hepsi tek listede toplanır, hesap bazında filtrelenebilir.
 */

const ACCOUNT_TYPES = {
  PERSONAL: { label: 'Kişisel hesap', icon: Users, cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  LOCATION_GROUP: { label: 'Lokasyon grubu', icon: Building2, cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  USER_GROUP: { label: 'Kullanıcı grubu', icon: Users, cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  ORGANIZATION: { label: 'Organizasyon', icon: Building2, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const typeInfo = (t) => ACCOUNT_TYPES[t] || { label: t || '—', icon: Store, cls: 'bg-gray-100 text-gray-600 border-gray-200' };

/**
 * Yanıt gövdesi JSON değilse (nginx/proxy HTML hata sayfası) mesaj kaybolup
 * boş hata kutusu görünmesin diye status + axios mesajına düşeriz.
 */
const errOf = (e) => {
  const d = e?.response?.data;
  if (d && typeof d === 'object') return d;
  return { message: `${e?.response?.status || ''} ${e?.message || 'Bilinmeyen hata'}`.trim() };
};

const nfmt = new Intl.NumberFormat('tr-TR');

/** Excel hücresine yazılabilir tek satır: boş dizi/null yerine boş metin. */
const cell = (v) => (v === null || v === undefined ? '' : v);
const list = (arr) => (Array.isArray(arr) ? arr.filter(Boolean).join(', ') : '');

/**
 * Bir günün çalışma saati. `hours` hiç yoksa (işletme saat girmemiş) boş
 * bırakılır — "Kapalı" yazmak yanlış olur. Saat girilmiş ama o gün periyodu
 * yoksa gerçekten kapalıdır.
 */
const dayHours = (l, day) => {
  if (!l.hours?.length) return '';
  const ranges = l.hours.find((h) => h.day === day)?.ranges || [];
  return ranges.length ? ranges.join(' / ') : 'Kapalı';
};

const gdate = (d) => (d && d.year ? `${String(d.day || 1).padStart(2, '0')}.${String(d.month || 1).padStart(2, '0')}.${d.year}` : '');
const gtime = (t) => (t && (t.hours != null || t.minutes != null)
  ? `${String(t.hours || 0).padStart(2, '0')}:${String(t.minutes || 0).padStart(2, '0')}`
  : '');


function SortHeader({ label, col, sort, onClick }) {
  const active = sort.key === col;
  return (
    <button
      onClick={() => onClick(col)}
      className={`inline-flex items-center gap-1 uppercase tracking-wide ${active ? 'text-gray-900' : 'hover:text-gray-700'}`}
    >
      {label}
      <ArrowUpDown className={`w-3 h-3 ${active ? 'text-blue-500' : 'text-gray-300'}`} />
      {active && <span className="text-[9px] text-blue-500">{sort.dir === 'asc' ? '↑' : '↓'}</span>}
    </button>
  );
}

/**
 * Lokasyonun vitrin karesi. Kapak varsa o, yoksa dış/iç mekân → ek fotoğraf
 * sırasıyla ilk bulunan gösterilir; hiç yoksa "fotoğraf yok" kutusu.
 * Görseller lh3.googleusercontent.com'da açık barındığı için token gerekmez,
 * ama referrer gönderilirse Google bazen 403 döndürür — no-referrer şart.
 */
function LocationThumb({ media, loading, title }) {
  const [broken, setBroken] = useState(false);
  if (loading) return <div className="w-11 h-11 rounded-lg bg-gray-100 animate-pulse shrink-0" />;
  const src = media?.preview?.thumb;
  if (!src || broken) {
    return (
      <div
        className="w-11 h-11 rounded-lg bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center shrink-0"
        title={media ? 'Bu lokasyonda hiç fotoğraf yok' : 'Fotoğraf bilgisi alınamadı'}
      >
        <ImageOff className="w-4 h-4 text-gray-300" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={title || ''}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setBroken(true)}
      className="w-11 h-11 rounded-lg object-cover border border-gray-200 shrink-0 bg-gray-100"
    />
  );
}

/** Puan hücresi: puan yoksa "—", henüz gelmediyse iskelet. */
function RatingCell({ row, loading }) {
  if (loading) return <span className="inline-block w-14 h-3 rounded bg-gray-100 animate-pulse" />;
  if (!row?.rating) return <span className="text-gray-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
      <span className="font-medium text-gray-900">{row.rating.toFixed(1)}</span>
    </span>
  );
}

function ErrorBox({ err }) {
  if (!err) return null;
  const disabled = err.reason === 'SERVICE_DISABLED';
  return (
    <div className={`rounded-xl border p-4 text-sm ${disabled ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-rose-50 border-rose-200 text-rose-900'}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="space-y-1 min-w-0">
          <div className="font-medium">{err.where || err.error || (disabled ? 'Bu API projede kapalı' : 'Hata')}</div>
          <div className="opacity-90 break-words">{err.hint || err.message}</div>
        </div>
      </div>
    </div>
  );
}

export default function GbpLocations() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [fatal, setFatal] = useState(null);
  const [q, setQ] = useState('');
  const [accFilter, setAccFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeDraft, setScopeDraft] = useState([]);
  const [savingScope, setSavingScope] = useState(false);
  // Puanlar ayrı uçtan gelir (lokasyon başına bir v4 çağrısı) — liste beklemesin
  const [ratings, setRatings] = useState(null);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingsErr, setRatingsErr] = useState(null);
  // Fotoğraflar da lokasyon başına bir v4 çağrısı — puanlardan sonra sıraya girer
  const [photos, setPhotos] = useState(null);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photosErr, setPhotosErr] = useState(null);
  const [photoFilter, setPhotoFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'title', dir: 'asc' });
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('locations');

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;

  const load = useCallback(async (isRefresh) => {
    if (!brandId) { setFatal({ message: 'Önce bir marka seçin.' }); setLoading(false); return; }
    isRefresh ? setRefreshing(true) : setLoading(true);
    setFatal(null);
    try {
      const r = await api.get('/gbp-brand/locations', { headers: { 'X-Brand-ID': brandId } });
      setData(r.data);
      setScopeDraft(r.data?.scope || []);
      // Kapsam değişince eski hesap filtresi geçersiz kalabilir
      setAccFilter('all');
    } catch (e) {
      setFatal(errOf(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [brandId]);

  useEffect(() => { load(false); }, [load]);

  /**
   * Liste geldikten sonra zenginleştirme: önce puanlar, ARDINDAN fotoğraflar
   * (82 lokasyon ≈ 3,5 sn + 3,5 sn). İkisi de lokasyon başına bir v4 çağrısı
   * yapıyor; paralel koşturmak v4'ün dakikalık kotasını zorladığı için
   * bilerek sıraya alındı — tablo bu arada zaten dolu.
   */
  useEffect(() => {
    if (!data?.connected || data.needsReconnect || !data.locations?.length) return;
    let alive = true;
    const byId = (arr) => {
      const m = {};
      (arr || []).forEach((x) => { m[x.id] = x; });
      return m;
    };

    (async () => {
      setRatingsLoading(true);
      setRatingsErr(null);
      try {
        const r = await api.get('/gbp-brand/ratings', { headers: { 'X-Brand-ID': brandId } });
        if (!alive) return;
        setRatings(byId(r.data?.ratings));
        if (r.data?.errors?.length) setRatingsErr(r.data.errors[0]);
      } catch (e) {
        if (alive) setRatingsErr(errOf(e));
      } finally {
        if (alive) setRatingsLoading(false);
      }

      if (!alive) return;
      setPhotosLoading(true);
      setPhotosErr(null);
      try {
        const r = await api.get('/gbp-brand/photos', { headers: { 'X-Brand-ID': brandId } });
        if (!alive) return;
        setPhotos(byId(r.data?.photos));
        if (r.data?.errors?.length) setPhotosErr(r.data.errors[0]);
      } catch (e) {
        if (alive) setPhotosErr(errOf(e));
      } finally {
        if (alive) setPhotosLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [data, brandId]);

  const connect = useCallback(async () => {
    try {
      // return=locations → OAuth bitince ayarlar sayfasına değil buraya döner
      const r = await api.get('/auth/gbp?return=locations', { headers: { 'X-Brand-ID': brandId } });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      setFatal(errOf(e));
    }
  }, [brandId]);

  const saveScope = useCallback(async () => {
    setSavingScope(true);
    setFatal(null);
    try {
      await api.put('/gbp-brand/scope', { accounts: scopeDraft }, { headers: { 'X-Brand-ID': brandId } });
      setScopeOpen(false);
      await load(true);
    } catch (e) {
      setFatal(errOf(e));
    } finally {
      setSavingScope(false);
    }
  }, [brandId, scopeDraft, load]);

  // Her render'da yeni dizi üretmesin — aşağıdaki useMemo'ların bağımlılığı
  const locations = useMemo(() => data?.locations || [], [data]);
  const allAccounts = data?.allAccounts || [];
  const scope = data?.scope || [];

  const cities = useMemo(() => {
    const set = new Map();
    locations.forEach((l) => { if (l.adminArea) set.set(l.adminArea, (set.get(l.adminArea) || 0) + 1); });
    return [...set.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'tr'));
  }, [locations]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase('tr');
    const rows = locations.filter((l) => {
      if (accFilter !== 'all' && l.accountName !== accFilter) return false;
      if (cityFilter !== 'all' && l.adminArea !== cityFilter) return false;
      // Fotoğraf filtresi ancak veri geldiyse uygulanır — yükleme sırasında
      // listeyi boşaltmasın diye photos null iken atlanır
      if (photoFilter !== 'all' && photos) {
        const p = photos[l.id];
        if (photoFilter === 'noCover' && p?.hasCover !== false) return false;
        if (photoFilter === 'noPhoto' && (p?.count || 0) > 0) return false;
      }
      if (!needle) return true;
      return [l.title, l.addressLine, l.locality, l.adminArea, l.storeCode, l.category, l.phone]
        .filter(Boolean)
        .some((v) => String(v).toLocaleLowerCase('tr').includes(needle));
    });

    // Puanı olmayan lokasyonlar sıralamanın hangi yönünde olursa olsun sona
    // düşer — yoksa "en düşük puanlılar" listesi puansızlarla dolar.
    const sign = sort.dir === 'asc' ? 1 : -1;
    const num = (l, key) => {
      if (key === 'photoCount') {
        const p = photos?.[l.id];
        // 0 fotoğraf gerçek bir değer (aranan durum), null "henüz gelmedi" demek
        return p ? p.count : null;
      }
      const r = ratings?.[l.id];
      const v = key === 'rating' ? r?.rating : r?.reviewCount;
      return typeof v === 'number' && (key !== 'rating' || v > 0) ? v : null;
    };
    return [...rows].sort((a, b) => {
      if (sort.key === 'title') return sign * a.title.localeCompare(b.title, 'tr');
      const av = num(a, sort.key);
      const bv = num(b, sort.key);
      if (av === null && bv === null) return a.title.localeCompare(b.title, 'tr');
      if (av === null) return 1;
      if (bv === null) return -1;
      return sign * (av - bv) || a.title.localeCompare(b.title, 'tr');
    });
  }, [locations, q, accFilter, cityFilter, photoFilter, sort, ratings, photos]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      // Puan/yorumda yüksekten başlamak, fotoğrafta ise AZ olandan başlamak
      // faydalı — aranan şey "hangi lokasyonun görseli eksik".
      : { key, dir: key === 'title' || key === 'photoCount' ? 'asc' : 'desc' }));
  }, []);

  // Kapsamdaki lokasyonların ağırlıklı ortalaması (puanı olanlar üzerinden)
  const ratingSummary = useMemo(() => {
    if (!ratings) return null;
    const rows = filtered.map((l) => ratings[l.id]).filter((r) => r && r.rating && r.reviewCount);
    if (!rows.length) return null;
    const totalReviews = rows.reduce((a, r) => a + r.reviewCount, 0);
    const weighted = rows.reduce((a, r) => a + r.rating * r.reviewCount, 0) / totalReviews;
    return { avg: weighted, totalReviews, rated: rows.length };
  }, [ratings, filtered]);

  /** Görsel eksiği özeti — filtredeki lokasyonlar üzerinden. */
  const photoSummary = useMemo(() => {
    if (!photos) return null;
    const rows = locations.map((l) => photos[l.id]).filter(Boolean);
    if (!rows.length) return null;
    return {
      total: rows.reduce((a, p) => a + p.count, 0),
      noCover: rows.filter((p) => !p.hasCover).length,
      noPhoto: rows.filter((p) => !p.count).length,
    };
  }, [photos, locations]);

  /**
   * Excel çıktısı — ekranda GÖRÜNEN satırlar (filtre + sıralama neyse o).
   * Üç sayfa: künye, fotoğraf kırılımı, özel günler. Puan/fotoğraf sütunları
   * ayrı uçlardan geldiği için ikisi de yüklenmeden düğme açılmaz; yoksa
   * kullanıcı sessizce boş sütunlarla dosya indirir.
   */
  const exportExcel = useCallback(() => {
    if (!filtered.length) return;

    const kunye = filtered.map((l) => {
      const r = ratings?.[l.id];
      const p = photos?.[l.id];
      const row = {
        'Mağaza': l.title,
        'Mağaza Kodu': cell(l.storeCode),
        'Durum': statusTR(l.status),
        'Puan': typeof r?.rating === 'number' && r.rating > 0 ? Number(r.rating.toFixed(2)) : '',
        'Yorum Sayısı': typeof r?.reviewCount === 'number' ? r.reviewCount : '',
        'Fotoğraf Sayısı': p ? p.count : '',
        'Kapak Görseli': p ? (p.hasCover ? 'Var' : 'Yok') : '',
        'Birincil Kategori': cell(l.category),
        'Ek Kategoriler': list(l.additionalCategories),
        'Telefon': cell(l.phone),
        'Diğer Telefonlar': list(l.additionalPhones),
        'Web Sitesi': cell(l.websiteUri),
        'Adres': cell(l.addressLine),
        'İlçe': cell(l.locality),
        'İl': cell(l.adminArea),
        'Posta Kodu': cell(l.postalCode),
        'Ülke': cell(l.country),
        'Enlem': cell(l.lat),
        'Boylam': cell(l.lng),
      };
      DAY_ORDER.forEach((d) => { row[DAY_TR[d]] = dayHours(l, d); });
      return {
        ...row,
        'Özel Gün Sayısı': l.specialHours?.length || 0,
        'Açıklama': cell(l.description),
        'Etiketler': list(l.labels),
        'Hizmet Bölgesi': list(l.serviceArea),
        'Gönderi Atılabilir': l.canOperateLocalPost ? 'Evet' : 'Hayır',
        'Maps Bağlantısı': cell(l.mapsUri),
        'Yorum Bağlantısı': cell(l.newReviewUri),
        'Place ID': cell(l.placeId),
        'Lokasyon ID': l.id,
        'GBP Hesabı': cell(l.accountLabel),
        'Hesap ID': String(l.accountName || '').split('/').pop(),
      };
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(kunye);
    // Sütun genişlikleri: metin alanları geniş, sayılar dar
    ws['!cols'] = Object.keys(kunye[0] || {}).map((k) => ({
      wch: k === 'Mağaza' ? 46
        : ['Adres', 'Açıklama', 'Web Sitesi', 'Maps Bağlantısı', 'Yorum Bağlantısı', 'Ek Kategoriler'].includes(k) ? 34
          : Math.max(12, Math.min(22, k.length + 4)),
    }));
    XLSX.utils.book_append_sheet(wb, ws, 'Mağazalar');

    // Fotoğraf kırılımı — hangi mağazada hangi tür kare eksik
    if (photos) {
      const usedCats = new Set();
      filtered.forEach((l) => Object.keys(photos[l.id]?.categories || {}).forEach((c) => usedCats.add(c)));
      const cats = [
        ...MEDIA_CATEGORY_ORDER.filter((c) => usedCats.has(c)),
        ...[...usedCats].filter((c) => !MEDIA_CATEGORY_ORDER.includes(c)),
      ];
      const foto = filtered.map((l) => {
        const p = photos[l.id];
        const row = {
          'Mağaza': l.title,
          'Toplam Fotoğraf': p ? p.count : '',
          'Kapak Görseli': p ? (p.hasCover ? 'Var' : 'Yok') : '',
        };
        cats.forEach((c) => { row[catTR(c)] = p?.categories?.[c] || 0; });
        return row;
      });
      const wsF = XLSX.utils.json_to_sheet(foto);
      wsF['!cols'] = Object.keys(foto[0] || {}).map((k) => ({ wch: k === 'Mağaza' ? 46 : Math.max(12, k.length + 4) }));
      XLSX.utils.book_append_sheet(wb, wsF, 'Fotoğraf Kırılımı');
    }

    // Özel günler (tatil/istisna saatleri) — tanımlı olan mağazalar için
    const ozel = [];
    filtered.forEach((l) => {
      (l.specialHours || []).forEach((sh) => {
        ozel.push({
          'Mağaza': l.title,
          'Başlangıç': gdate(sh.startDate),
          'Bitiş': gdate(sh.endDate || sh.startDate),
          'Durum': sh.closed ? 'Kapalı' : 'Açık',
          'Açılış': sh.closed ? '' : gtime(sh.openTime),
          'Kapanış': sh.closed ? '' : gtime(sh.closeTime),
        });
      });
    });
    if (ozel.length) {
      const wsO = XLSX.utils.json_to_sheet(ozel);
      wsO['!cols'] = [{ wch: 46 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, wsO, 'Özel Günler');
    }

    const slug = (data?.brand?.name || 'marka').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const bugun = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `gbp-lokasyonlar-${slug}-${bugun}.xlsx`);
  }, [filtered, ratings, photos, data]);

  const hasFilter = q.trim() || accFilter !== 'all' || cityFilter !== 'all' || photoFilter !== 'all';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lokasyonlar Google'dan alınıyor…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5 max-w-[1600px] mx-auto">
      {/* Başlık */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Google Business Profile — Lokasyonlar
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {data?.brand?.name ? <span className="font-medium text-gray-700">{data.brand.name}</span> : null}
            {data?.connection?.googleEmail ? <> · bağlı hesap <span className="font-mono text-xs">{data.connection.googleEmail}</span></> : null}
            {data?.fetchedAt ? <> · canlı veri, {new Date(data.fetchedAt).toLocaleTimeString('tr-TR')}</> : null}
          </p>
        </div>
        {data?.connected && (
          <div className="flex items-center gap-2">
            {allAccounts.length > 0 && (
              <button
                onClick={() => { setScopeDraft(scope); setScopeOpen((v) => !v); }}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${scopeOpen ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                <SlidersHorizontal className="w-4 h-4" />
                Hesap kapsamı
                {scope.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[11px]">{scope.length}</span>
                )}
              </button>
            )}
            {tab === 'locations' && (
              <button
                onClick={exportExcel}
                disabled={!filtered.length || ratingsLoading || photosLoading}
                title={ratingsLoading || photosLoading
                  ? 'Puan ve fotoğraf verisi yükleniyor — bitince indirilebilir'
                  : 'Ekrandaki mağazaları tüm künye bilgileriyle indirir'}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {ratingsLoading || photosLoading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Download className="w-4 h-4" />}
                Excel indir
                {filtered.length ? (
                  <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px]">{filtered.length}</span>
                ) : null}
              </button>
            )}
            <button
              onClick={() => load(true)}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Yenile
            </button>
          </div>
        )}
      </div>

      {/* Sekmeler — liste canlı API'den, rapor gecelik fotoğraflardan okur */}
      {data?.connected && (
        <div className="flex items-center gap-1 border-b border-gray-200">
          {[
            ['locations', 'Lokasyonlar', MapPin],
            ['report', 'Rapor', BarChart3],
          ].map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm -mb-px border-b-2 ${
                tab === key
                  ? 'border-blue-600 text-blue-700 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      )}

      {tab === 'report' ? (
        <GbpReportTab brandId={brandId} brandName={data?.brand?.name} />
      ) : (
       <>
      {/* Hesap kapsamı — markaya hangi GBP hesaplarının ait olduğu */}
      {scopeOpen && data?.connected && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 space-y-3">
          <div>
            <div className="font-medium text-gray-900 text-sm">
              Bu markada hangi Google hesapları gösterilsin?
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Bağlı Google hesabı birden çok müşterinin lokasyon grubunu görebilir.
              Seçim yaparsanız sayfada yalnızca seçtikleriniz listelenir; hiçbirini
              seçmezseniz erişilen tüm hesaplar görünür.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {allAccounts.map((a) => {
              const on = scopeDraft.includes(a.name);
              return (
                <button
                  key={a.name}
                  onClick={() => setScopeDraft((prev) => (
                    prev.includes(a.name) ? prev.filter((x) => x !== a.name) : [...prev, a.name]
                  ))}
                  className={`flex items-start gap-2 text-left rounded-lg border p-3 transition ${on ? 'border-blue-400 bg-white ring-1 ring-blue-200' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                >
                  <span className={`w-4 h-4 mt-0.5 shrink-0 rounded border flex items-center justify-center ${on ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                    {on && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-gray-900 truncate">{a.label || a.id}</span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">
                      {typeInfo(a.type).label}{a.role ? ` · ${a.role}` : ''}
                    </span>
                    <span className="block text-[11px] text-gray-400 font-mono truncate">{a.id}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={saveScope}
              disabled={savingScope}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {savingScope && <Loader2 className="w-4 h-4 animate-spin" />} Kaydet
            </button>
            <button
              onClick={() => setScopeOpen(false)}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50"
            >
              Vazgeç
            </button>
            {scopeDraft.length > 0 && (
              <button
                onClick={() => setScopeDraft([])}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Seçimi temizle (tüm hesaplar)
              </button>
            )}
          </div>
        </div>
      )}

      <ErrorBox err={fatal} />

      {/* Bağlantı yok */}
      {data && !data.connected && (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center">
          <Link2 className="w-8 h-8 text-gray-400 mx-auto mb-3" />
          <div className="text-gray-900 font-medium">Bu markada Google Business Profile bağlantısı yok</div>
          <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
            Google hesabınızı bağladığınızda, o hesabın eriştiği bütün lokasyonlar —
            kişisel profiller ve lokasyon grupları dahil — burada listelenir.
          </p>
          <button
            onClick={connect}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            <Link2 className="w-4 h-4" /> Google ile bağlan
          </button>
        </div>
      )}

      {/* Yeniden bağlanma gerekiyor */}
      {data?.needsReconnect && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">Google bağlantısı yenilenmeli</div>
            <div className="opacity-90">{data.errors?.[0]?.message}</div>
            <button onClick={connect} className="mt-2 underline font-medium">Yeniden bağlan</button>
          </div>
        </div>
      )}

      {data?.connected && !data.needsReconnect && (
        <>
          {/* Hesap özeti */}
          {data.accounts?.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {data.accounts.map((a) => {
                const ti = typeInfo(a.type);
                const Icon = ti.icon;
                const active = accFilter === a.name;
                return (
                  <button
                    key={a.name}
                    onClick={() => setAccFilter(active ? 'all' : a.name)}
                    className={`text-left rounded-xl border p-4 transition ${active ? 'border-blue-400 ring-2 ring-blue-100 bg-blue-50/40' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{a.label || a.id}</div>
                        <span className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full border text-[11px] ${ti.cls}`}>
                          <Icon className="w-3 h-3" /> {ti.label}
                        </span>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-2xl font-semibold text-gray-900 leading-none">{a.locationCount}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">lokasyon</div>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-gray-500">
                      {a.role && <span className="px-1.5 py-0.5 rounded bg-gray-100">{a.role}</span>}
                      {a.verificationState && (
                        <span className={`px-1.5 py-0.5 rounded ${a.verificationState === 'VERIFIED' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                          {a.verificationState === 'VERIFIED' ? 'Doğrulanmış' : a.verificationState}
                        </span>
                      )}
                    </div>
                    {a.error && <div className="mt-2 text-[11px] text-rose-600 line-clamp-2">{a.error}</div>}
                  </button>
                );
              })}
            </div>
          )}

          {/* Hesap bazlı hatalar */}
          {data.errors?.length > 0 && (
            <div className="space-y-2">
              {data.errors.map((e, i) => <ErrorBox key={i} err={e} />)}
            </div>
          )}

          {/* Filtreler */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Lokasyon adı, adres, şehir, mağaza kodu…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              />
            </div>
            <select
              value={accFilter}
              onChange={(e) => setAccFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              <option value="all">Tüm hesaplar ({data.accounts?.length || 0})</option>
              {data.accounts?.map((a) => (
                <option key={a.name} value={a.name}>{a.label || a.id} ({a.locationCount})</option>
              ))}
            </select>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              <option value="all">Tüm iller ({cities.length})</option>
              {cities.map(([c, n]) => <option key={c} value={c}>{c} ({n})</option>)}
            </select>
            <select
              value={photoFilter}
              onChange={(e) => setPhotoFilter(e.target.value)}
              disabled={!photos}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white disabled:opacity-50"
              title={photos ? '' : 'Fotoğraflar henüz yükleniyor'}
            >
              <option value="all">Fotoğraf: tümü</option>
              <option value="noCover">Kapağı olmayanlar{photoSummary ? ` (${photoSummary.noCover})` : ''}</option>
              <option value="noPhoto">Hiç fotoğrafı olmayanlar{photoSummary ? ` (${photoSummary.noPhoto})` : ''}</option>
            </select>
            {hasFilter && (
              <button
                onClick={() => { setQ(''); setAccFilter('all'); setCityFilter('all'); setPhotoFilter('all'); }}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
              >
                <X className="w-3.5 h-3.5" /> Temizle
              </button>
            )}
            <div className="text-sm text-gray-500 ml-auto flex items-center gap-3">
              {ratingsLoading && !ratings && (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> puanlar alınıyor
                </span>
              )}
              {photosLoading && !photos && (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> fotoğraflar alınıyor
                </span>
              )}
              {photoSummary && (
                <span className="inline-flex items-center gap-1.5">
                  <ImageIcon className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-900">{nfmt.format(photoSummary.total)}</span>
                  <span className="text-xs text-gray-400">fotoğraf</span>
                  {photoSummary.noCover > 0 && (
                    <button
                      onClick={() => setPhotoFilter(photoFilter === 'noCover' ? 'all' : 'noCover')}
                      className={`text-xs px-1.5 py-0.5 rounded border ${photoFilter === 'noCover'
                        ? 'bg-amber-100 border-amber-300 text-amber-900'
                        : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'}`}
                      title="Kapak fotoğrafı olmayan lokasyonları göster"
                    >
                      {photoSummary.noCover} kapaksız
                    </button>
                  )}
                </span>
              )}
              {ratingSummary && (
                <span className="inline-flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  <span className="font-medium text-gray-900">{ratingSummary.avg.toFixed(2)}</span>
                  <span className="text-xs text-gray-400">
                    {nfmt.format(ratingSummary.totalReviews)} yorum · {ratingSummary.rated} lokasyon
                  </span>
                </span>
              )}
              <span>
                <span className="font-medium text-gray-900">{filtered.length}</span>
                {filtered.length !== locations.length && <> / {locations.length}</>} lokasyon
              </span>
            </div>
          </div>

          {ratingsErr && (
            <ErrorBox err={{ where: 'Puan & yorumlar alınamadı', ...ratingsErr }} />
          )}
          {photosErr && (
            <ErrorBox err={{ where: 'Fotoğraflar alınamadı', ...photosErr }} />
          )}

          {/* Tablo */}
          {locations.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center text-sm text-gray-500">
              Bu Google hesabının eriştiği hiç lokasyon yok.
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-4 py-3 font-medium">
                      <SortHeader label="Lokasyon" col="title" sort={sort} onClick={toggleSort} />
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <SortHeader label="Puan" col="rating" sort={sort} onClick={toggleSort} />
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <SortHeader label="Yorum" col="reviewCount" sort={sort} onClick={toggleSort} />
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <SortHeader label="Foto" col="photoCount" sort={sort} onClick={toggleSort} />
                    </th>
                    <th className="px-4 py-3 font-medium">Hesap</th>
                    <th className="px-4 py-3 font-medium">İl / İlçe</th>
                    <th className="px-4 py-3 font-medium">Adres</th>
                    <th className="px-4 py-3 font-medium">Kategori</th>
                    <th className="px-4 py-3 font-medium">Telefon</th>
                    <th className="px-4 py-3 font-medium text-right">Bağlantılar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((l) => (
                    <tr
                      key={l.name}
                      onClick={() => setSelected(l)}
                      className="hover:bg-blue-50/40 align-top cursor-pointer group"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-3">
                          <LocationThumb
                            media={photos?.[l.id]}
                            loading={!photos && !photosErr}
                            title={l.title}
                          />
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 flex items-center gap-1">
                              {l.title || '—'}
                              <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-500 transition" />
                            </div>
                            <div className="text-[11px] text-gray-400 font-mono mt-0.5">{l.id}</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {l.status && l.status !== 'OPEN' && (
                                <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px]">
                                  {l.status === 'CLOSED_PERMANENTLY' ? 'Kalıcı kapalı' : l.status === 'CLOSED_TEMPORARILY' ? 'Geçici kapalı' : l.status}
                                </span>
                              )}
                              {photos?.[l.id] && !photos[l.id].hasCover && (
                                <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[11px]">
                                  {photos[l.id].count ? 'kapak yok' : 'fotoğraf yok'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <RatingCell row={ratings?.[l.id]} loading={ratingsLoading && !ratings} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {ratingsLoading && !ratings
                          ? <span className="inline-block w-8 h-3 rounded bg-gray-100 animate-pulse" />
                          : ratings?.[l.id]?.reviewCount
                            ? nfmt.format(ratings[l.id].reviewCount)
                            : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {!photos
                          ? (photosErr
                              ? <span className="text-gray-300">—</span>
                              : <span className="inline-block w-8 h-3 rounded bg-gray-100 animate-pulse" />)
                          : photos[l.id]?.count
                            ? <span className="inline-flex items-center gap-1.5">
                                <ImageIcon className="w-3.5 h-3.5 text-gray-400" />
                                {nfmt.format(photos[l.id].count)}
                              </span>
                            : <span className="inline-flex items-center gap-1.5 text-amber-600">
                                <ImageOff className="w-3.5 h-3.5" /> 0
                              </span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-700 truncate max-w-[180px]">{l.accountLabel || '—'}</div>
                        <span className={`inline-block mt-1 px-1.5 py-0.5 rounded border text-[11px] ${typeInfo(l.accountType).cls}`}>
                          {typeInfo(l.accountType).label}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                        {l.adminArea || '—'}
                        {l.locality && <div className="text-xs text-gray-500">{l.locality}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-[280px]">{l.addressLine || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{l.category || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {l.phone ? <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3 text-gray-400" />{l.phone}</span> : '—'}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          {l.websiteUri && (
                            <a href={l.websiteUri} target="_blank" rel="noreferrer" title="Web sitesi"
                               className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700">
                              <Globe className="w-4 h-4" />
                            </a>
                          )}
                          {l.mapsUri && (
                            <a href={l.mapsUri} target="_blank" rel="noreferrer" title="Google Haritalar'da aç"
                               className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700">
                              <MapPin className="w-4 h-4" />
                            </a>
                          )}
                          {l.newReviewUri && (
                            <a href={l.newReviewUri} target="_blank" rel="noreferrer" title="Yorum yazma bağlantısı"
                               className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700">
                              <Star className="w-4 h-4" />
                            </a>
                          )}
                          {!l.websiteUri && !l.mapsUri && !l.newReviewUri && <span className="text-gray-300">—</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-4 py-10 text-center text-sm text-gray-500">
                        Filtreye uyan lokasyon yok.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-gray-400 flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />
            Liste her açılışta Google API'sinden canlı çekilir; veritabanına yazılmaz.
            Detay için bir lokasyona tıklayın.
          </p>
        </>
      )}

       </>
      )}

      {selected && (
        <GbpLocationModal
          location={selected}
          brandId={brandId}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
